import React, { useMemo, useState } from "react";
import { Select } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { loadOrder, saveOrder } from "../utils/layoutStore";
import { getActiveYm, setActiveYm } from "../utils/activeMonth";
import {
  JALALI_MONTHS, currentJalali, jalaaliMonthLength, jalaaliToISO,
} from "../utils/dateUtils";

/**
 * دورهٔ ماهانهٔ لیست‌های تولید — «فقط همین ماه را نشان بده».
 *
 * چرا: لیستِ برش/ضایعات/تولید با گذشتِ ماه‌ها بلند می‌شود و کاربر عملاً همیشه
 * با ماهِ جاری کار می‌کند. این انتخاب per-user و per-صفحه ذخیره می‌شود تا با
 * هر بار ورود همان دورهٔ قبلی بیاید، نه پیش‌فرض.
 *
 * `useMonthScope(pageId)` → { value, node, range:[fromISO,toISO]|null, inRange(iso) }
 * مقدارِ «همه» ⇒ range=null یعنی بدونِ فیلتر.
 */
export function monthOptions() {
  const cj = currentJalali();
  const out = [{ value: "all", label: "همهٔ ماه‌ها" }];
  // از ماهِ جاری به عقب تا ۱۴ ماه — جدیدتر بالاتر، همان چیزی که واقعاً لازم است
  let y = cj.year, m = cj.month;
  for (let i = 0; i < 14; i += 1) {
    out.push({ value: `${y}-${m}`, label: `${JALALI_MONTHS[m - 1]} ${y}` });
    m -= 1; if (m < 1) { m = 12; y -= 1; }
  }
  return out;
}

export function useMonthScope(pageId) {
  const cj = currentJalali();
  const dflt = `${cj.year}-${cj.month}`;
  // ماهِ فعالِ مشترک (v2.132.0) بر انتخابِ قبلیِ همین صفحه مقدم است — مگر آن انتخاب «همه» بوده
  const [value, setValue] = useState(() => {
    const own = loadOrder("pmonth", pageId);
    if (own === "all") return own;
    const a = getActiveYm();
    return a ? `${a.year}-${a.month}` : (own || dflt);
  });

  const parsed = useMemo(() => {
    if (value === "all") return null;
    const [jy, jm] = String(value).split("-").map(Number);
    return jy && jm ? { jy, jm } : null;
  }, [value]);
  const range = useMemo(() => (parsed
    ? [jalaaliToISO(parsed.jy, parsed.jm, 1),
      jalaaliToISO(parsed.jy, parsed.jm, jalaaliMonthLength(parsed.jy, parsed.jm))]
    : null), [parsed]);

  const inRange = (iso) => {
    if (!range) return true;
    if (!iso) return false;              // بی‌تاریخ در فیلترِ ماهانه نشان داده نمی‌شود
    const d = String(iso).slice(0, 10);
    return d >= range[0] && d <= range[1];
  };

  const node = (
    <Select
      value={value}
      onChange={(v) => {
        saveOrder("pmonth", pageId, v); setValue(v);
        if (v !== "all") { const [y, m] = String(v).split("-"); setActiveYm(y, m); }
      }}
      options={monthOptions()}
      style={{ minWidth: 150 }}
      suffixIcon={<CalendarOutlined />}
      popupMatchSelectWidth={false}
    />
  );

  /** «مالِ این ماه است؟» — ماهِ انتخاب‌شده یعنی **ماهِ محاسبهٔ حقوق** (خواستهٔ
      کاربر ۱۴۰۵/۰۶/۲۲): اگر ماهِ محاسبه صریحاً ست شده فقط همان ملاک است؛ وگرنه
      تاریخِ کار (همان قاعدهٔ `payroll_month_filter`ِ موتورِ حقوق). */
  const presentIn = (iso, payrollYear, payrollMonth) => {
    if (!parsed) return true;
    if (payrollYear && payrollMonth) {
      return payrollYear === parsed.jy && payrollMonth === parsed.jm;
    }
    return inRange(iso);
  };

  return { value, range, inRange, presentIn, jy: parsed?.jy, jm: parsed?.jm, node };
}
